import styled, { css } from 'styled-components';
import { observer } from 'mobx-react';
import { opacityHex } from '@honeybook/hbui/dist/utils/color.utils';
import is from 'styled-is';
import { mediumUp } from '@honeybook/hbui/dist/styles/breakpoints';
import { printCss } from '@honeybook/hbui/dist/styles/mixins';
import {
    ContentWidth,
    IContainerBlockStyles,
    PaddingSize
} from '../../../types/block-styles.types';
import { DnDWrapper } from '../../Builder/Block/Block.styles';
import { IChildLayout } from '../../../types/model.types';
import { getContentWidth, Screens } from '../../../utils/block.utils';

export const breakpoints = {
    l: 1200,
    m: 840,
    s: 440
};

export const paddingYMap = {
    [PaddingSize.LARGE]: 60,
    [PaddingSize.MEDIUM]: 40,
    [PaddingSize.SMALL]: 20,
    [PaddingSize.NONE]: 0,
    [PaddingSize.CUSTOM]: null
};

export function getScreenSize(containerWidth: number) {
    if (containerWidth >= breakpoints.l) {
        return Screens.XLARGE;
    }

    if (containerWidth >= breakpoints.m) {
        return Screens.LARGE;
    }

    if (containerWidth >= breakpoints.s) {
        return Screens.MEDIUM;
    }

    return Screens.SMALL;
}

function getGridColumnsWidth(
    autoFlow: string,
    styles: IContainerBlockStyles,
    resizeable: boolean,
    childBlocks?: IChildLayout[]
) {
    if (autoFlow === 'row' || !childBlocks || childBlocks.length === 1) {
        return css``;
    }

    const columnsTemplate = childBlocks
        .map(child => `${child.size}fr `)
        .join(`${resizeable ? `${styles.grid_gap}px ` : ''}`);

    return css`
        grid-template-columns: ${columnsTemplate};
    `;
}

export function getFlowDirection(
    screenSize: Screens,
    contentWidth: ContentWidth
) {
    return [Screens.MEDIUM, Screens.SMALL].includes(screenSize) ||
        contentWidth === ContentWidth.SMALL
        ? 'row'
        : 'column';
}

/*
    The content width depends on the container's layout.
    It determines the sizes of the different blocks on the screen.
    In a columns layout - the grid gaps are deducted from the container's width
    In a rows layout - the content width equals the container's width
 */
export function getContentWidthByLayout(
    styles: IContainerBlockStyles,
    canvasWidth: number,
    nChildren: number
) {
    const screenSize = getScreenSize(canvasWidth);
    const flowDirection = getFlowDirection(screenSize, styles.content_width);
    const contentWidth = getContentWidth(
        styles.content_width,
        screenSize,
        canvasWidth
    );

    return flowDirection === 'row'
        ? contentWidth
        : contentWidth - (nChildren - 1) * styles.grid_gap;
}

function getGridStyles(
    screenSize: Screens,
    containerBlockStylesProps: IContainerStylesProps
) {
    const { forceRow, styles: containerBlockStyles } =
        containerBlockStylesProps;

    const autoFlow = forceRow
        ? 'row'
        : getFlowDirection(screenSize, containerBlockStyles.content_width);
    const gap =
        autoFlow === 'row' || !containerBlockStylesProps.resizeable
            ? containerBlockStyles.grid_gap
            : 0;
    const widthInPxls = getContentWidth(
        containerBlockStyles.content_width,
        screenSize,
        containerBlockStylesProps.containerWidth
    );

    return css`
        max-width: ${widthInPxls === Infinity ? '' : `${widthInPxls}px`};
        width: 100%;
        grid-auto-flow: ${autoFlow};
        grid-auto-columns: 1fr;

        grid-gap: 20px;

        ${mediumUp} {
            ${gap ? `grid-gap: ${gap}px;` : ''}
            ${getGridColumnsWidth(
                autoFlow,
                containerBlockStylesProps.styles,
                containerBlockStylesProps.resizeable,
                containerBlockStylesProps.childBlocks
            )}
        }
    `;
}

interface IContainerStylesProps {
    containerWidth: number;
    styles: IContainerBlockStyles;
    resizeable: boolean;
    forceRow?: boolean;
    childBlocks?: IChildLayout[];
}

export function getContainerBlockStyles(
    containerBlockStylesProps: IContainerStylesProps
) {
    const screenSize = getScreenSize(containerBlockStylesProps.containerWidth);
    let paddingY =
        paddingYMap[containerBlockStylesProps.styles.padding_y] ??
        containerBlockStylesProps.styles.custom_padding_y;

    return css`
        ${getGridStyles(screenSize, containerBlockStylesProps)}
        transition: padding 0.2s;
        padding: ${paddingY}px 0;
    `;
}

function setBackgroundColorWithOverlay(
    styles: IContainerBlockStyles,
    backgroundColorKey: string,
    ignoreBackgroundOpacity: boolean
) {
    return css`
        position: absolute;
        z-index: -1;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        background-color: ${ignoreBackgroundOpacity
            ? styles[backgroundColorKey]
            : opacityHex(
                  styles[backgroundColorKey],
                  styles.background_color_opacity
              )};
    `;
}

export const StyledHorizontalContainer = observer(styled.div<{
    styles: IContainerBlockStyles;
    containerWidth: number;
    forceRow?: boolean;
    childBlocks?: IChildLayout[];
    isBlur?: boolean;
    isMobileApp?: boolean;
}>`
    display: grid;
    margin: auto;
    position: relative;
    grid-auto-flow: row;

    ${({ containerWidth, styles, forceRow, childBlocks }) =>
        getContainerBlockStyles({
            containerWidth,
            styles,
            resizeable: false,
            forceRow,
            childBlocks
        })}

    ${is('isBlur')`
        filter: blur(4px);
    `};

    ${is('isMobileApp')`
        padding-top: 0;
    `};

    ${printCss(`
        display: flex;
        justify-content: center;
        max-width: unset;
    `)}
`);

export const ResizeableStyledHorizontalContainer = observer(styled.div<{
    styles: IContainerBlockStyles;
    containerWidth: number;
    forceRow?: boolean;
    childBlocks?: IChildLayout[];
}>`
    display: grid;
    margin: auto;
    position: relative;
    ${({ containerWidth, styles, forceRow, childBlocks }) =>
        getContainerBlockStyles({
            containerWidth,
            styles,
            resizeable: true,
            forceRow,
            childBlocks
        })}
`);

export const HorizontalDnDWrapper = styled(DnDWrapper)`
    transition: opacity 0.2s;
`;

export const BackgroundImageWrapper = styled.div<{
    opacity: number;
}>`
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background-size: cover;
    background-position: 50%;
    opacity: ${props => props.opacity / 100};
`;

export const StyledBackgroundColorWrapper = styled.div<{
    styles: IContainerBlockStyles;
    backgroundColorKey: string;
    borderRadius?: number;
    ignoreBackgroundOpacity?: boolean;
}>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    ${({ styles, backgroundColorKey, ignoreBackgroundOpacity }) =>
        setBackgroundColorWithOverlay(
            styles,
            backgroundColorKey,
            ignoreBackgroundOpacity
        )};
    border-radius: ${props => props.borderRadius || null}px;
`;

export const BackgroundColorWrapper = observer(StyledBackgroundColorWrapper);
